<script setup lang="ts">
import { FormDropdownOption } from '@/types/form/form-dropdown/form-dropdown';
import {
  FormDropdownCurtainEmits,
  FormDropdownCurtainProps,
} from '@/types/form/form-dropdown/form-dropdown-curtain';
import { computed, ref } from 'vue';

const props = defineProps<FormDropdownCurtainProps>();
const emit = defineEmits<FormDropdownCurtainEmits>();
const keywords = ref<string>();
const modalSearchEl = ref<HTMLInputElement>();
const focusSearch = () => modalSearchEl.value?.focus();
const onOptionClick = (option: FormDropdownOption, index: number) => {
  emit('option-click', option, index);
};
const onClear = () => {
  emit('clear');
};
const shownOptions = computed(() => {
  if (!keywords.value) return props.options;
  return props.options.filter((option) => {
    return new RegExp(keywords.value!, 'ig').test(option.text);
  });
});
defineExpose({ focusSearch });
</script>
<template>
  <div class="f-dropdown-curtain">
    <div class="f-dropdown-curtain__search" v-if="options.length > 10">
      <div class="f-dropdown-curtain__input">
        <icon name="search" size="xxs" />
        <input type="text" v-model="keywords" ref="modalSearchEl" />
      </div>
    </div>
    <div class="f-dropdown-curtain__options">
      <div
        v-if="clearable"
        :class="['f-dropdown-curtain__option clear-option', { selected: !selectedOption }]"
        @click="onClear"
      >
        {{ clearText }}
      </div>
      <div
        v-for="(option, index) in shownOptions"
        :key="index"
        :class="[
          'f-dropdown-curtain__option',
          { selected: option.value === selectedOption?.value },
        ]"
        @click="onOptionClick(option, index)"
      >
        {{ option.text }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.f-dropdown-curtain {
  &__search {
    background: var(--item-curtain-back-color, $grey-light);
    padding: 16px;
  }
  &__input {
    height: 32px;
    border: 1px solid #929292;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    gap: 8px;
    background: $white;
    color: $black;
    border-radius: 999px;
    input {
      border: none !important;
      outline-width: 0 !important;
      padding: 0 !important;
      margin-bottom: 0 !important;
      flex-grow: 1;
      &::placeholder {
        color: $grey;
        @include h9;
      }
    }
    .e-icon {
      flex-shrink: 0;
      color: $grey;
      svg {
        width: 16px;
        height: auto;
      }
    }
  }
  &__option {
    padding: 8px 16px;
    cursor: pointer;
    @include h8;
    user-select: none;
    text-align: center;

    &:hover {
      background-color: rgba($white, 0.15);
    }

    &.selected {
      background-color: #6b9171;
      color: $white;
    }

    &.clear-option {
      color: #929292;
      &.selected {
        background-color: #f5f5f5;
      }
    }
  }

  @include desktop {
    &__option {
      text-align: left;
    }
  }
}
</style>
